/* eslint-disable @typescript-eslint/no-explicit-any */
import { DefaultRoute } from "../router/routes";
import { encodeId } from "./common";
import { authService } from "@src/@api-services/auth"; // 🔥 Import AuthService

// ** Existing utility functions (unchanged)
export const isObjEmpty = (obj: Record<string, any>): boolean =>
  Object.keys(obj).length === 0;

export const kFormatter = (num: number): string | number =>
  num > 999 ? `${(num / 1000).toFixed(1)}k` : num;

export const htmlToString = (html: string): string =>
  html.replace(/<\/?[^>]+(>|$)/g, "");

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const formatDate = (
  value: string | number | Date,
  formatting: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  }
): string => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

export const DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

export const formatDateTime = (
  value: string | number | Date,
  formatting: Intl.DateTimeFormatOptions = DATE_TIME_FORMAT
): string => formatDate(value, formatting);

export const formatDateOrEmpty = (
  value?: string | number | Date | null,
  empty = "N/A",
  formatting?: Intl.DateTimeFormatOptions
): string => {
  if (value === null || value === undefined || value === "") return empty;
  return formatDate(value, formatting);
};

export const formatDateTimeOrEmpty = (
  value?: string | number | Date | null,
  empty = "N/A"
): string => formatDateOrEmpty(value, empty, DATE_TIME_FORMAT);

export const formatDateToMonthShort = (
  value: string | number | Date,
  toTimeForCurrentDay = true
): string => {
  const date = new Date(value);
  let formatting: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: "numeric", minute: "numeric" };
  }

  return new Intl.DateTimeFormat("en-US", formatting).format(date);
};

// ** 🔥 IMPROVED: Use AuthService instead of manual localStorage
export const isUserLoggedIn = (): boolean => {
  try {
    
    // ✅ Use AuthService methods instead of manual localStorage
    const userData = authService.getUserData();
    const accessToken = authService.getToken();
    

    
    // ✅ AuthService already handles all validation logic
    const isValid = !!(userData && userData.id && accessToken);
    
    return isValid;
    
  } catch (error) {
    console.error('🔴 Error checking login status:', error);
    return false;
  }
};

// ** 🔥 IMPROVED: Use AuthService for getting user data
export const getUserData = (): Record<string, any> | null => {
  try {
    
    // ✅ Use AuthService method - no manual localStorage access
    const userData = authService.getUserData();
    
    if (!userData) {
      return null;
    }
    

    
    return userData;
    
  } catch (error) {
    console.error('🔴 Error getting user data:', error);
    return null;
  }
};

// ** 🔥 IMPROVED: Safe Home Route Function
export const getHomeRouteForLoggedInUser = (userRole: string): string => {
  try {
    
    if (!userRole || typeof userRole !== 'string') {
      return DefaultRoute;
    }
    
    const normalizedRole = userRole.toLowerCase().trim();
    
    let route = DefaultRoute;
    
    switch (normalizedRole) {
      case "admin":
      case "super admin":
      case "administrator":
        route = DefaultRoute; // "/"
        break;
      case "client":
      case "customer":
        route = "/access-control";
        break;
      case "manager":
      case "supervisor":
      case "user":
      case "employee":
      case "staff":
        route = DefaultRoute;
        break;
      default:
        console.warn(`🚫 Unknown user role: ${userRole}, using default`);
        route = DefaultRoute;
        break;
    }
    
    return route;
    
  } catch (error) {
    console.error('🔴 Error determining home route:', error);
    return DefaultRoute;
  }
};

// ** 🔥 IMPROVED: Safe 2FA Router
export const get2FARouter = (userId: string, email: string): string => {
  try {
    if (!userId || !email) {
      console.error('🚫 Missing userId or email for 2FA route');
      return '/login';
    }
    
    const encrypted = encodeId(userId, email);
    
    if (!encrypted) {
      console.error('🚫 Failed to encode 2FA parameters');
      return '/login';
    }
    
    return `/2fa?authKey=${encodeURIComponent(encrypted)}`;
    
  } catch (error) {
    console.error('🔴 Error generating 2FA route:', error);
    return '/login';
  }
};

// ** 🔥 IMPROVED: Use AuthService for logout
export const handleCompleteLogout = (): void => {
  
  try {
    // ✅ Use AuthService method instead of manual cleanup
    authService.clearSession();
    
    
    // ** Force redirect after cleanup
    setTimeout(() => {
      console.log("🔄 Redirecting to login...");
      window.location.href = "/login";
    }, 100);
    
  } catch (error) {
    console.error("🔴 Logout error:", error);
    
    // ** Emergency fallback - manual cleanup
    try {
      authService.removeAllAuthData();
    } catch (clearError) {
      console.error("Emergency clear failed:", clearError);
      // Last resort
      localStorage.clear();
      sessionStorage.clear();
    }
    
    // ** Force redirect anyway
    window.location.href = "/login";
  }
};

// ** 🔥 NEW: Centralized session validation
export const hasValidSession = (): boolean => {
  try {
    // ✅ Use AuthService method
    return authService.hasValidSession();
  } catch (error) {
    console.error("Error validating session:", error);
    return false;
  }
};

// ** 🔥 NEW: Check if authenticated (wrapper for AuthService)
export const isAuthenticated = (): boolean => {
  try {
    // ✅ Use AuthService method
    return authService.isAuthenticated();
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

// ** React Select Theme Colors
const isDarkSelectTheme = (): boolean => {
  if (typeof document === "undefined") return false;
  return document.body.classList.contains("dark-layout");
};

export const selectThemeColors = (theme: any): any => {
  if (!isDarkSelectTheme()) {
    return {
      ...theme,
      colors: {
        ...theme.colors,
        primary25: "#7367f01a",
        primary: "#7367f0",
        neutral10: "#7367f0",
        neutral20: "#ededed",
        neutral30: "#ededed",
      },
    };
  }

  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary: "#7367f0",
      primary25: "rgba(115, 103, 240, 0.16)",
      primary50: "rgba(115, 103, 240, 0.28)",
      primary75: "rgba(115, 103, 240, 0.4)",
      neutral0: "#ffffff",
      neutral5: "#283046",
      neutral10: "#3b4253",
      neutral20: "#3b4253",
      neutral30: "#3b4253",
      neutral40: "#676d7d",
      neutral50: "#b4b7bd",
      neutral60: "#b4b7bd",
      neutral70: "#d0d2d6",
      neutral80: "#d0d2d6",
      neutral90: "#f8f8f8",
    },
  };
};